import { useMemo } from "react";
import { Text, View } from "react-native";
import { ArrowRight, CheckCircle2, Heart } from "lucide-react-native";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { Hero } from "./hero/Hero";
import { CarePillActionButton } from "./CarePillActionButton";
import { NeutralStepPill } from "./NeutralStepPill";
import { StepperSection } from "./Stepper";
import {
  getTaskSections,
  useHomepageState,
} from "@src/packages/patients/hooks/useHomepageState";
import type { HomeRelationship } from "@src/packages/homepage/experience/resolveHomeExperience";
import type { PlanAction } from "../plan.types";
import {
  resolveCarrierJourneyModel,
  type CarrierJourneyScenario,
} from "./carrierJourney";

const heroBackground =
  require("@src/apps/moon/assets/images/precycle-hero-bronze-gradient.webp") as number;

type Props = {
  scenario: CarrierJourneyScenario;
  relationship: HomeRelationship;
  onAction: (action: PlanAction) => void;
};

export function CarrierGuidedHome({ scenario, relationship, onAction }: Props) {
  const { data: homepageState } = useHomepageState();
  const taskSections = getTaskSections(homepageState);
  const model = useMemo(
    () =>
      resolveCarrierJourneyModel({
        scenario,
        hasConnection:
          relationship.partnerConnected ||
          relationship.leadCounterparty !== null,
        hasLegalReview: relationship.processBadge === "legal",
      }),
    [relationship, scenario],
  );

  const sectionIds = model.sections.map((section) => `carrier.${section.id}`);
  const completedCount = sectionIds.filter(
    (id) => taskSections.completed[id],
  ).length;
  const primaryAction = model.sections.find(
    (section) => section.action,
  )?.action;

  return (
    <>
      <Hero
        eyebrow={{ label: model.eyebrow, icon: Heart }}
        title={model.title}
        summary={model.summary}
        metrics={[
          {
            label: "Progress",
            value: `${completedCount} of ${model.sections.length}`,
          },
          { label: "Phase", value: model.phaseLabel },
          { label: "Connection", value: model.connectionLabel },
        ]}
        metricVariant="tags"
        backgroundSource={heroBackground}
        primaryAction={
          primaryAction
            ? {
                label: primaryAction.label,
                onPress: () => onAction(primaryAction),
              }
            : undefined
        }
        style={{ marginTop: 4 }}
      />

      <Text
        className="font-dm-sans"
        style={{
          marginTop: 28,
          marginBottom: 16,
          paddingHorizontal: 4,
          fontSize: 18,
          lineHeight: 24,
          color: "#6B5548",
          fontWeight: "700",
        }}
      >
        Your next steps, with the right owner at each stage
      </Text>

      <View style={{ position: "relative", overflow: "visible" }}>
        {model.sections.map((section, index) => {
          const sectionId = `carrier.${section.id}`;
          return (
            <StepperSection
              key={sectionId}
              id={sectionId}
              stepLabel={section.stepLabel}
              showRail={index > 0}
            >
              <View
                style={{
                  ...(GLASS_CARD_STYLE as object),
                  padding: 18,
                  gap: 10,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 18,
                    lineHeight: 23,
                    fontWeight: "700",
                    color: "#2A2522",
                  }}
                >
                  {section.title}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    lineHeight: 19,
                    fontWeight: "500",
                    color: "#6B5548",
                  }}
                >
                  {section.body}
                </Text>

                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 8,
                    marginTop: 4,
                  }}
                >
                  {section.bullets.map((bullet) => (
                    <View
                      key={bullet}
                      style={{
                        width: "100%",
                        flexDirection: "row",
                        alignItems: "flex-start",
                        gap: 7,
                      }}
                    >
                      <CheckCircle2
                        size={14}
                        color="#8B3A1A"
                        strokeWidth={1.8}
                        style={{ marginTop: 2 }}
                      />
                      <Text
                        className="font-dm-sans"
                        style={{
                          flex: 1,
                          fontSize: 12.5,
                          lineHeight: 18,
                          fontWeight: "600",
                          color: "#6B5548",
                        }}
                      >
                        {bullet}
                      </Text>
                    </View>
                  ))}
                </View>

                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    marginTop: 4,
                  }}
                >
                  <NeutralStepPill label={`Owner · ${section.owner}`} />
                  {section.action ? (
                    <CarePillActionButton
                      label={section.action.label}
                      trailingIcon={ArrowRight}
                      onPress={() => onAction(section.action!)}
                      variant="secondary"
                      flexBasis={120}
                    />
                  ) : null}
                </View>
              </View>
            </StepperSection>
          );
        })}
      </View>
    </>
  );
}
